import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';

import Adhesion from '../../../atoms/adhesion';
import CardResource from '../../../molecules/profile/card-resource';
import CardResourceContainer from '../../../molecules/profile/card-resource-container';
import FeesAndExpenses from '../../../molecules/profile/fees-and-expenses';
import FooterDisclosures from '../../../atoms/footer-disclosures';
import GeographicDistribution from '../../../molecules/profile/geographic-distribution';
import HeaderQuoteBar from '../../../molecules/profile/header-quote-bar';
import InvestmentFacts from '../../../molecules/profile/investment-facts';
import InvestmentHoldings from '../../../molecules/profile/investment-holdings';
import InvestmentStrategy from '../../../molecules/profile/investment-strategy';
import MarketCapitalization from '../../../molecules/profile/market-capitalization';
import MorningstarFactsRatings from '../../../molecules/profile/morningstar-facts-ratings';
import MaturityAllocation from '../../../molecules/profile/maturity-allocation';
import PageHeader from '../../../molecules/profile/page-header';
import PageMain from '../../../molecules/profile/page-main';
import PageSideRail, { DisclosurePrint } from '../../../molecules/profile/page-side-rail';
import SectionDivider from '../../../atoms/section-divider';
import sectionDividerOptions from '../../../../helpers/statics/section-divider-options';
import transformInvestmentFacts from '../../../../stores/profile/modules/transform-investment-facts';
import useStore from '../../../../stores/profile/use-after-tax-annuity-store';
import PerformanceReturns from '../../../molecules/profile/performance-returns';
import { className } from '../../../../utils/GlobalCssProxy';
import HypotheticalGrowth from '../../../molecules/profile/hypothetical-growth';
import AssetAllocation from '../../../molecules/profile/asset-allocation';
import SectorDiversification from '../../../molecules/profile/sector-diversification';
import TopFiveCountries from '../../../molecules/profile/top-five-countries';
import RisksDisclosure from '../../../molecules/profile/risks-disclosure';
import useDetectPrint from 'react-detect-print';
import { getIsDynamicGreetingShown } from '../../../molecules/profile/dynamic-greeting/dynamic-greeting-helper';

const AfterTaxAnnuity = () => {
  const store = useStore();
  const xrefIsLoaded = store.xref.isLoaded;
  const quoteData = store.quote.isLoaded ? store.quote.data : {};
  const participantHoldingsData = (store.participantHoldings && store.participantHoldings.isLoaded)
    ? store.participantHoldings.data
    : {};
  const showDynamicGreeting = participantHoldingsData && getIsDynamicGreetingShown(participantHoldingsData);
  const xrefData = xrefIsLoaded ? store.xref.data : {};
  const marketCapData = store.marketCapitalization.isLoaded
    ? store.marketCapitalization.data
    : {};
  const maturityAllocationData = store.maturityAllocation.isLoaded
    ? store.maturityAllocation.data
    : [];
  const isAssetClassFixedIncome = quoteData.assetClass === 'Fixed Income';
  const isAssetClassUSEquity = quoteData.assetClass === 'U.S. Equity';
  const isNonPropATA = !store.isProprietaryFund;
  const disclosureList = store.disclosureList.isLoaded ? store.disclosureList.data : [];
  const printing = useDetectPrint();
  const printingFeesExpenses = [disclosureList.find(item => { return item?.component === 'performance-returns'; })];

  return (
    <div data-template="AfterTaxAnnuity">
      <PageHeader
        displayAsFundType="Personal Annuities"
        sectionNavOptions={[
          sectionDividerOptions.performance,
          sectionDividerOptions.allocation,
          sectionDividerOptions.characteristics,
          sectionDividerOptions.resources
        ]}
      >
        <HeaderQuoteBar>
          <HeaderQuoteBar.DailyNav
            value={Math.round(quoteData.priceLast * 100) / 100}
            asOfDate={quoteData.priceAsOfDate}
            formatOverride="$0,0.00"
          />
          <HeaderQuoteBar.DayChange
            value={Math.round(quoteData.priceChange * 100) / 100}
            changePercent={quoteData.priceChangePercent}
            formatOverride="$0,0.00"
          />
          <HeaderQuoteBar.YtdReturn
            value={quoteData.yearToDateReturn}
            asOfDate={quoteData.yearToDateReturnAsOfDate}
            responsive={printing ? undefined : 1}
          />
          <HeaderQuoteBar.NetAndTotalExpenseRatio
            netRatio={quoteData.netExpenseRatio}
            totalRatio={quoteData.totalExpenseRatio}
            responsive={printing ? undefined : 2}
          />
          <HeaderQuoteBar.MorningstarRating
            value={quoteData.morningstarOverallRating}
            asOfDate={quoteData.ratingAsOfDate}
            category={quoteData.morningstarCategory}
            fundsInCategory={quoteData.morningstarOverallRatingOufOfNumber}
            responsive={printing ? undefined : 3}
          />
          <HeaderQuoteBar.AssetClass responsive={printing ? undefined : 4}>
            {quoteData.assetClass}
          </HeaderQuoteBar.AssetClass>
        </HeaderQuoteBar>
      </PageHeader>
      <PageMain>
        <SectionDivider
          labelText={sectionDividerOptions.performance.label}
          id={sectionDividerOptions.performance.id}
          isDynamicGreetingShown={showDynamicGreeting}
          dynamicGreetingParticipantHoldings={participantHoldingsData}
        >
          <Row>
            <Col>
              <PerformanceReturns
                isAlternateInceptionDate={true}
                {...store.performanceReturns}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <DisclosurePrint disclosureList={printingFeesExpenses} avoidPageBreak />
            </Col>
          </Row>
          <Row>
            <Col md={8}>
              <HypotheticalGrowth showPriceChartOnly={true} />
            </Col>
            <Col md={4} {...className('d-none', 'd-md-block')}>
              <Adhesion.Performance />
            </Col>
          </Row>
          <Row>
            <Col>
              <MorningstarFactsRatings
                facts={store.morningstarFactsRatings.data}
                heading="Morningstar Facts &amp; Ratings"
              />
            </Col>
            <Col md={4}>
              <FeesAndExpenses
                expenses={
                  store.feesExpenses.isLoaded ? store.feesExpenses.data : {}
                }
                heading="Fees &amp; Expenses"
                fundType={store.fundType || ''}
              />
            </Col>
          </Row>
        </SectionDivider>
        <Row>
          <Col>
            <RisksDisclosure />
          </Col>
        </Row>
        <SectionDivider
          labelText={sectionDividerOptions.allocation.label}
          id={sectionDividerOptions.allocation.id}
        >
          <Row>
            <Col md={isAssetClassUSEquity ? 12 : 6}>
              <AssetAllocation
                allocations={
                  store.assetAllocation.isLoaded
                    ? store.assetAllocation.data.items
                    : []
                }
                asOfDate={
                  store.assetAllocation.isLoaded
                    ? store.assetAllocation.data.asOfDate
                    : null
                }
                ticker={xrefData.symbol || null}
              />
            </Col>
            <Col md={isAssetClassUSEquity ? 0 : 6}>
              {!isAssetClassUSEquity && (
                <MaturityAllocation
                  maturityAllocations={maturityAllocationData}
                  ticker={xrefData.symbol}
                />
              )}
            </Col>
          </Row>
          <Row>
            <Col md={8}>
              <SectorDiversification
                ticker={xrefData.symbol}
                isFundNonProp={isNonPropATA}
              />
            </Col>
            <Col md={4} {...className('d-none', 'd-md-block')}>
              <Adhesion.Allocation />
            </Col>
          </Row>
          <Row>
            <Col md={isAssetClassFixedIncome ? 0 : 6}>
              {!isAssetClassFixedIncome && (
                <MarketCapitalization
                  asOfDate={marketCapData.asOfDate}
                  marketCapitalization={marketCapData.items}
                  ticker={xrefData.symbol}
                />
              )}
            </Col>
            <Col md={isAssetClassFixedIncome ? 12 : 6}>
              <GeographicDistribution
                hideSwitchAndMap={true}
                isFundNonProp={isNonPropATA}
              />
            </Col>
          </Row>
          <Row>
            <Col md={8}>
              <InvestmentHoldings
                holdings={
                  store.investmentHoldings.isLoaded
                    ? store.investmentHoldings.data
                    : []
                }
                detailedHoldingsUrl={
                  store.isProprietaryFund ? store.detailedHoldingsUrl : null
                }
                isFundNonProp={isNonPropATA}
              />
            </Col>
            <Col md={4}>
              <TopFiveCountries />
            </Col>
          </Row>
        </SectionDivider>
        <SectionDivider
          labelText={sectionDividerOptions.characteristics.label}
          id={sectionDividerOptions.characteristics.id}
        >
          <Row>
            <Col md={7}>
              <InvestmentStrategy
                heading="Investment Description"
                {...(store.investmentStrategy.data || {})}
              >
                {store.investmentStrategy.isLoaded
                  ? store.investmentStrategy.data.investmentStrategy
                  : ''}
              </InvestmentStrategy>
            </Col>
            <Col md={5}>
              <InvestmentFacts
                header="Investment Facts"
                facts={
                  store.investmentFacts.isLoaded
                    ? transformInvestmentFacts(
                      store.investmentFacts.data,
                      store.fundType,
                      store.assetClass
                    )
                    : [] // prettier-ignore
                }
                ticker={xrefData.symbol ? xrefData.symbol : null}
                hideDisclosure={true}
                isNonPropATA={isNonPropATA}
              ></InvestmentFacts>
            </Col>
          </Row>
        </SectionDivider>
        <SectionDivider
          labelText={sectionDividerOptions.resources.label}
          id={sectionDividerOptions.resources.id}
        >
          <CardResourceContainer>
            <CardResource.SimpleLink
              bodyText="Personal Annuities"
              data-analytics-componenttype="column-component"
              data-analytics-id="COL_Learn"
              data-analytics-section="component"
              href="https://www.tiaa.org/public/retire/financial-products/annuities/personal-annuities"
              iconVariant="Learn"
              titleText="Learn"
            />
            {/* <CardResource.SimpleLink
              bodyText="Quarterly Performance & Asset Allocation Models"
              data-analytics-componenttype="column-component"
              data-analytics-id="COL_Tools"
              data-analytics-section="component"
              href="https://www.tiaa.org/public/pdf/IMG_IVA-Model_wPerformance.pdf"
              iconVariant="Calculator"
              titleText="Tools"
            /> */}
            <CardResource.SimpleLink
              bodyText="Asset Allocation Guide"
              data-analytics-componenttype="column-component"
              data-analytics-id="COL_Planning"
              data-analytics-section="component"
              href="https://www.tiaa.org/public/pdf/Asset_Allocation_Guide_A38473.pdf"
              iconVariant="Planning"
              titleText="Tools"
            />
            <CardResource.SimpleLink
              bodyText="Investment Performance"
              data-analytics-componenttype="column-component"
              data-analytics-id="COL_Research"
              data-analytics-section="component"
              href="https://www.tiaa.org/public/investment-performance/aftertax"
              iconVariant="Research"
              titleText="Research"
            />
          </CardResourceContainer>
        </SectionDivider>
        <FooterDisclosures />
        <div {...className('admaster-number', 'd-print-none')}>X-3240718P-Q0923W</div>
        <DisclosurePrint disclosureList = {disclosureList} admasterNumber="X-3240718P-Q0923W" />
      </PageMain>
      <PageSideRail />
    </div>
  );
};

export default AfterTaxAnnuity;
